import React, { useMemo, useState } from 'react';
import { Select, Space, Statistic, Row, Col, Alert, Tag, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { Column } from '@ant-design/plots';
import TradeRouteMap from '../components/TradeRouteMap';
import LoadingIndicator from '../components/LoadingIndicator';
import ErrorAlert from '../components/ErrorAlert';
import ExportDialog from '../components/ExportDialog';
import { useTradeRoutes } from '../hooks/useTradeData';
import type { TradeRoute } from '../types';
import './Trade.scss';
const { Option } = Select;
/**
 * 贸易路径页面
 */
const Trade: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);
  const [selectedProductType, setSelectedProductType] = useState<string | undefined>(undefined);
  const [selectedOriginCity, setSelectedOriginCity] = useState<string | undefined>(undefined);
  const [exportDialogVisible, setExportDialogVisible] = useState(false);
  // 获取贸易路径数据
  const { data: rawRoutes, isLoading, error } = useTradeRoutes({
    year: selectedYear,
    product_type: selectedProductType,
    origin_city: selectedOriginCity,
  });
  // 【修复点】增加安全检查，确保 routes 永远是数组
  const routes = useMemo(() => {
    return Array.isArray(rawRoutes) ? rawRoutes : [];
  }, [rawRoutes]);
  // 计算统计数据
  const statistics = useMemo(() => {
    if (!routes || routes.length === 0) {
      return {
        totalRoutes: 0,
        totalVolume: 0,
        totalValue: 0,
        avgVolume: 0,
      };
    }
    const totalVolume = routes.reduce((sum: number, r: TradeRoute) => sum + r.trade_volume_tons, 0);
    const totalValue = routes.reduce((sum: number, r: TradeRoute) => sum + (r.trade_value_usd || 0), 0);
    return {
      totalRoutes: routes.length,
      totalVolume,
      totalValue,
      avgVolume: totalVolume / routes.length,
    };
  }, [routes]);
  // 获取可用的年份列表
  const availableYears = useMemo(() => {
    if (!routes) return [];
    try {
        const years = (Array.from(new Set(routes.map((r: TradeRoute) => r.year))) as number[]).sort((a, b) => b - a);
        return years;
    } catch (e) {
        return [];
    }
  }, [routes]);
  // 获取可用的产品类型列表
  const productTypes = React.useMemo(() => {
    if (!routes) return [];
    try {
        return Array.from(new Set(routes.map((r: TradeRoute) => r.product_type))).sort();
    } catch (e) {
        return [];
    }
  }, [routes]);
  // 获取可用的起始城市列表
  const originCities = React.useMemo(() => {
    if (!routes) return [];
    try {
        return Array.from(new Set(routes.map((r: TradeRoute) => r.origin_city))).sort();
    } catch (e) {
        return [];
    }
  }, [routes]);
  // 按目的地国家统计贸易量（前10）
  const volumeByDestination = useMemo(() => {
    if (!routes || routes.length === 0) return [];
    const volumeMap = new Map<string, number>();
    routes.forEach((route: TradeRoute) => {
      const country = route.destination_country;
      volumeMap.set(country, (volumeMap.get(country) || 0) + route.trade_volume_tons);
    });
    return Array.from(volumeMap.entries())
      .map(([country, volume]) => ({ country, volume }))
      .sort((a, b) => b.volume - a.volume)
      .slice(0, 10);
  }, [routes]);
  // 按产品类型统计贸易量
  const volumeByProduct = useMemo(() => {
    if (!routes || routes.length === 0) return [];
    const volumeMap = new Map<string, number>();
    routes.forEach((route: TradeRoute) => {
      const product = route.product_type;
      volumeMap.set(product, (volumeMap.get(product) || 0) + route.trade_volume_tons);
    });
    return Array.from(volumeMap.entries())
      .map(([product, volume]) => ({ product, volume }))
      .sort((a, b) => b.volume - a.volume);
  }, [routes]);
  if (isLoading) {
    return <LoadingIndicator />;
  }
  if (error) {
    return <ErrorAlert message="加载贸易路径数据失败" description={error.message} />;
  }
  // 贸易数据可导出字段
  const tradeExportFields = [
    { value: 'year', label: '年份' },
    { value: 'origin_country', label: '起始国家' },
    { value: 'origin_city', label: '起始城市' },
    { value: 'destination_country', label: '目的地国家' },
    { value: 'destination_city', label: '目的地城市' },
    { value: 'product_type', label: '产品类型' },
    { value: 'trade_volume_tons', label: '贸易量（吨）' },
    { value: 'trade_value_usd', label: '贸易额（美元）' },
    { value: 'transport_method', label: '运输方式' },
  ];
  // Chart config for light theme
  const chartTheme = {
    label: {
      style: { fill: '#666' }
    },
    xAxis: {
      label: {
        style: { fill: '#666' },
        autoRotate: true,
      },
      grid: {
        line: { style: { stroke: '#eee' } }
      }
    },
    yAxis: {
      label: {
        style: { fill: '#666' }
      },
      title: {
        style: { fill: '#666' },
        text: '贸易量 (吨)',
      },
      grid: {
        line: { style: { stroke: '#eee' } }
      }
    },
    legend: {
      itemValue: { style: { fill: '#666' } },
      itemName: { style: { fill: '#666' } }
    },
    tooltip: {
      domStyles: {
        'g2-tooltip': {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          color: '#333',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #eee',
          borderRadius: '8px',
          backdropFilter: 'blur(4px)',
        }
      }
    }
  };
  return (
    <div className="trade-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h1 style={{ margin: 0 }}>贸易路径分析</h1>
          <p style={{ marginTop: '8px', marginBottom: 0 }}>
            展示钒产品从主要产地销往全球的贸易路径和贸易量分布
          </p>
        </div>
        <Button
          className="glass-button"
          icon={<DownloadOutlined />}
          onClick={() => setExportDialogVisible(true)}
        >
          导出数据
        </Button>
      </div>
      <div className="glass-card filter-section">
        <Space size="large" wrap>
          <div>
            <label style={{ marginRight: '8px' }}>年份：</label>
            <Select
              style={{ width: 120 }}
              placeholder="全部年份"
              allowClear
              value={selectedYear}
              onChange={setSelectedYear}
            >
              {availableYears.map(year => (
                <Option key={String(year)} value={String(year)}>{String(year)}</Option>
              ))}
            </Select>
          </div>
          <div>
            <label style={{ marginRight: '8px' }}>产品类型：</label>
            <Select
              style={{ width: 150 }}
              placeholder="全部产品"
              allowClear
              value={selectedProductType}
              onChange={setSelectedProductType}
            >
              {productTypes.map(type => (
                <Option key={String(type)} value={String(type)}>{String(type)}</Option>
              ))}
            </Select>
          </div>
          <div>
            <label style={{ marginRight: '8px' }}>起始城市：</label>
            <Select
              style={{ width: 150 }}
              placeholder="全部城市"
              allowClear
              value={selectedOriginCity}
              onChange={setSelectedOriginCity}
            >
              {originCities.map(city => (
                <Option key={String(city)} value={String(city)}>{String(city)}</Option>
              ))}
            </Select>
          </div>
        </Space>
      </div>
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <div className="glass-card">
            <Statistic
              title="贸易路径数量"
              value={statistics.totalRoutes}
              suffix={<span style={{ fontSize: '14px', color: '#999' }}>条</span>}
            />
          </div>
        </Col>
        <Col span={6}>
          <div className="glass-card">
            <Statistic
              title="总贸易量"
              value={statistics.totalVolume}
              precision={0}
              suffix={<span style={{ fontSize: '14px', color: '#999' }}>吨</span>}
            />
          </div>
        </Col>
        <Col span={6}>
          <div className="glass-card">
            <Statistic
              title="总贸易额"
              value={statistics.totalValue}
              precision={0}
              prefix="$"
            />
          </div>
        </Col>
        <Col span={6}>
          <div className="glass-card">
            <Statistic
              title="平均贸易量"
              value={statistics.avgVolume}
              precision={0}
              suffix={<span style={{ fontSize: '14px', color: '#999' }}>吨</span>}
            />
          </div>
        </Col>
      </Row>
      <div className="glass-card">
        <div className="ant-card-head" style={{ borderBottom: '1px solid #f0f0f0', marginBottom: '16px', paddingBottom: '8px' }}>
          <div className="ant-card-head-title">贸易路径地图</div>
        </div>
        {routes && routes.length > 0 ? (
          <>
            <div style={{ marginBottom: '16px' }}>
              <Space>
                <span style={{ fontSize: '12px', color: '#666' }}>图例：</span>
                <Tag color="red">V2O5</Tag>
                <Tag color="blue">FeV50</Tag>
                <Tag color="green">FeV80</Tag>
                <Tag color="orange">VN</Tag>
                <span style={{ fontSize: '12px', color: '#999', marginLeft: '16px' }}>
                  弧线粗细表示贸易量大小
                </span>
              </Space>
            </div>
            <TradeRouteMap routes={routes} />
          </>
        ) : (
          <Alert
            message="暂无数据"
            description="当前筛选条件下没有贸易路径数据"
            type="info"
            showIcon
          />
        )}
      </div>
      <Row gutter={16}>
        <Col span={12}>
          <div className="glass-card">
            <div className="ant-card-head" style={{ borderBottom: '1px solid #f0f0f0', marginBottom: '16px', paddingBottom: '8px' }}>
              <div className="ant-card-head-title">目的地国家贸易量排名（前10）</div>
            </div>
            {volumeByDestination.length > 0 ? (
              <Column
                data={volumeByDestination}
                xField="country"
                yField="volume"
                label={{
                  position: 'top',
                  formatter: (datum: any) => {
                    const val = typeof datum === 'number' ? datum : (datum?.volume ?? 0);
                    return `${(val / 1000).toFixed(1)}k`;
                  },
                  ...chartTheme.label
                }}
                xAxis={chartTheme.xAxis}
                yAxis={chartTheme.yAxis}
                tooltip={chartTheme.tooltip}
                color="#40a9ff"
              />
            ) : (
              <Alert message="暂无数据" type="info" showIcon />
            )}
          </div>
        </Col>
        <Col span={12}>
          <div className="glass-card">
            <div className="ant-card-head" style={{ borderBottom: '1px solid #f0f0f0', marginBottom: '16px', paddingBottom: '8px' }}>
              <div className="ant-card-head-title">产品类型贸易量分布</div>
            </div>
            {volumeByProduct.length > 0 ? (
              <Column
                data={volumeByProduct}
                xField="product"
                yField="volume"
                label={{
                  position: 'top',
                  formatter: (datum: any) => {
                    const val = typeof datum === 'number' ? datum : (datum?.volume ?? 0);
                    return `${(val / 1000).toFixed(1)}k`;
                  },
                  ...chartTheme.label
                }}
                xAxis={chartTheme.xAxis}
                yAxis={chartTheme.yAxis}
                tooltip={chartTheme.tooltip}
                color="#52c41a"
              />
            ) : (
              <Alert message="暂无数据" type="info" showIcon />
            )}
          </div>
        </Col>
      </Row>
      <ExportDialog
        visible={exportDialogVisible}
        onClose={() => setExportDialogVisible(false)}
        dataType="trade"
        availableFields={tradeExportFields}
        currentFilters={{
          year: selectedYear,
          product_type: selectedProductType,
          origin_city: selectedOriginCity,
        }}
      />
    </div>
  );
};
export default Trade;
